import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import { Grid, DialogContent } from "@mui/material"

import i18n from "config/i18n"
import { useAppDispatch } from "redux/hooks"
import {
  withModalStep,
  withModalDebt,
  withModalPaymentAmount,
  withPaymentMethods,
} from "hocs/features"
import {
  closeDialog,
  updateDialogStep,
} from "redux/features/dialog/dialogSlice"
import PollingService from "redux/api/pollingService"
import { showAlert } from "redux/features/notice/noticeSlice"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import NewPayment from "components/Payment/Pay/New"
import NewTransfer from "components/Payment/Pay/Transfer"
import { PAYMENT_STEP_INITIAL, PAYMENT_STEP_TRANSFER } from "config"
import { DebtModalType, DebtType, PaymentMethodList } from "types"

type Props = DebtModalType & {
  amount: number
  paymentMethods: PaymentMethodList
  debt: DebtType
  step: number
}

const PaymentPayModal = ({
  debt,
  amount,
  paymentMethods,
  step = PAYMENT_STEP_INITIAL,
}: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const isBackHidden = step !== PAYMENT_STEP_TRANSFER

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: i18n.t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
  }

  const handlePaymentCreateFailure = (error: string) => {
    dispatch(
      showAlert({
        message: error,
      }),
    )
  }

  const handleBack = () => {
    switch (step) {
      case PAYMENT_STEP_TRANSFER:
        dispatch(updateDialogStep({ step: PAYMENT_STEP_INITIAL }))
        return
      default:
        return
    }
  }

  const STEPS = [
    {
      label: PAYMENT_STEP_INITIAL,
      title: t("features.payment.form.title"),
      component: (
        <NewPayment
          debtId={debt.id}
          amount={amount}
          paymentMethods={paymentMethods.filter((method) =>
            debt.company?.legal_name !== "Fresh Finance OÜ"
              || method.value === "Swedbank")
          }
          onToggle={() =>
            dispatch(updateDialogStep({ step: PAYMENT_STEP_TRANSFER }))
          }
          onFailure={(error) => handlePaymentCreateFailure(error)}
        />
      ),
    },
    {
      label: PAYMENT_STEP_TRANSFER,
      title: t("features.payment.form.title"),
      component: (
        <NewTransfer
          amount={amount}
          debt={debt}
          onBanklink={() =>
            dispatch(updateDialogStep({ step: PAYMENT_STEP_INITIAL }))
          }
        />
      ),
    },
  ]

  return (
    <DialogContent>
      <Back hidden={isBackHidden} onClick={handleBack} />
      <Close onClick={handleClose} />
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Grid>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withModalDebt,
  withModalPaymentAmount,
  withPaymentMethods,
)(PaymentPayModal)
